import { localizarPontoNoMundo } from './ancoragem'
import { adicionarAncoragem } from './ancoragens'
import { adicionarCorda } from './cordas'
import { adicionarObjeto, gerarIdPadrao } from './operacoes'
import type {
  CenaTabletop,
  GeradorIdTabletop,
  ReferenciaPonto,
  TipoAncoragem,
  VetorTabletop,
} from './tipos'

type V = VetorTabletop

const somar = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const dist = (a: V, b: V) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])

/**
 * Calcula a posição no mundo do vértice central de uma ancoragem (V, W, M)
 * ou da ponta livre de uma fita simples:
 * - Fita simples (1 ponto): pende para baixo (cerca de 50 cm abaixo do ponto de fixação).
 * - Equalização (2+ pontos): posiciona-se abaixo do ponto médio das fixações,
 *   a uma profundidade que busca um ângulo ideal de 60° a 75° entre as pernas mais distantes.
 */
export function posicaoVerticeAncoragem(
  cena: CenaTabletop,
  fixacoes: readonly ReferenciaPonto[],
): VetorTabletop {
  if (fixacoes.length === 0) return [0, 1, 0]

  const pontos = fixacoes
    .map((ref) => localizarPontoNoMundo(cena, ref))
    .filter((p): p is V => p !== null)

  if (pontos.length === 0) return [0, 1, 0]

  if (pontos.length === 1) {
    const p = pontos[0]!
    // Pende 50 cm para baixo; se estiver muito perto do solo (y < 0.6), joga um pouco para frente
    const quedaY = p[1] > 0.6 ? 0.5 : Math.max(0.15, p[1] * 0.5)
    const recuoZ = p[1] <= 0.6 ? 0.35 : 0
    return [p[0], Math.max(0.1, p[1] - quedaY), p[2] + recuoZ]
  }

  // Ponto médio
  const n = pontos.length
  const medio: V = [
    pontos.reduce((s, p) => s + p[0], 0) / n,
    pontos.reduce((s, p) => s + p[1], 0) / n,
    pontos.reduce((s, p) => s + p[2], 0) / n,
  ]

  // Envergadura máxima entre os pontos
  let maiorDist = 0
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = dist(pontos[i]!, pontos[j]!)
      if (d > maiorDist) maiorDist = d
    }
  }

  // Profundidade ideal para formar cerca de 65° a 75° de abertura: D = (span / 2) / tan(35°)
  const profundidade = Math.max(0.35, Math.min(1.5, (maiorDist / 2) / Math.tan((35 * Math.PI) / 180)))

  return [medio[0], Math.max(0.1, medio[1] - profundidade), medio[2]]
}

export interface ResultadoCriacaoAncoragem {
  readonly cena: CenaTabletop
  readonly idObjetoVertice: string
  readonly idAncoragem?: string
  readonly idFita?: string
}

/**
 * Cria a fita ou ancoragem equalizada (V, W, M) automaticamente:
 * - 1 ponto: cria um ponto de ancoragem (alça de fita) na ponta livre e a fita correspondente.
 * - 2 pontos: cria o vértice central equalizado em V e a ancoragem em V correspondente.
 * - 3 pontos: cria o vértice em W e a ancoragem em W.
 * - 4 pontos: cria o vértice em M e a ancoragem em M.
 */
export function criarAncoragemAutomatica(
  cena: CenaTabletop,
  fixacoes: readonly ReferenciaPonto[],
  tipo?: TipoAncoragem,
  gerarId: GeradorIdTabletop = gerarIdPadrao,
): ResultadoCriacaoAncoragem {
  if (fixacoes.length === 0) {
    throw new Error('É necessário ao menos um ponto de fixação.')
  }

  const posicao = posicaoVerticeAncoragem(cena, fixacoes)
  const idVertice = gerarId()

  const tipoReal: TipoAncoragem | null =
    tipo ?? (fixacoes.length === 2 ? 'V' : fixacoes.length === 3 ? 'W' : fixacoes.length >= 4 ? 'M' : null)

  const nomeVertice =
    fixacoes.length === 1 ? 'Ponta da fita' : `Vértice em ${tipoReal ?? 'V'}`

  // Cria o objeto que servirá como ponto de ancoragem no vértice/ponta livre
  let novaCena = adicionarObjeto(
    cena,
    {
      tipo: 'pontoAncoragem',
      nome: nomeVertice,
      transformacao: {
        posicao,
        rotacao: [0, 0, 0],
        escala: [1, 1, 1],
      },
      parametros: { alcaFita: 1 },
    },
    () => idVertice,
  )

  const refCentro: ReferenciaPonto = { idObjeto: idVertice, idPonto: 'olhal' }

  if (fixacoes.length === 1) {
    const idFita = gerarId()
    novaCena = adicionarCorda(
      novaCena,
      {
        funcao: 'fita',
        percurso: [fixacoes[0]!, refCentro],
        rotulo: 'Fita simples',
      },
      () => idFita,
    )
    return { cena: novaCena, idObjetoVertice: idVertice, idFita }
  }

  const idAncoragem = gerarId()
  const fixacoesFinais = fixacoes.slice(0, tipoReal === 'V' ? 2 : tipoReal === 'W' ? 3 : 4)

  novaCena = adicionarAncoragem(
    novaCena,
    {
      tipo: tipoReal!,
      fixacoes: fixacoesFinais,
      centro: refCentro,
      cargaKgf: 200,
      rotulo: `Ancoragem em ${tipoReal}`,
    },
    () => idAncoragem,
  )

  return { cena: novaCena, idObjetoVertice: idVertice, idAncoragem }
}
